/**
 * Diagnostics Config
 *
 * Threshold values for the network/multiplayer diagnostic panels —
 * defines what counts as "OK" (green), "WARN" (orange), or "BAD" (red)
 * for each metric. Tweaking these adjusts how lenient/strict the
 * diagnostic UI looks during stream demos.
 *
 * Helper functions (`thresholdColor`, `pingColor`) live in the legacy
 * file because they're pure rendering utilities, not config.
 *
 * Two-section file (defaults+dev-override pattern). See
 * `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface DiagnosticsThreshold {
  /** Below this → green (healthy). */
  ok: number;
  /** Below this → orange (warn); above → red. */
  warn: number;
}

export interface DiagnosticsConfig {
  /** Number of input corrections per second seen by the client. */
  corrections: DiagnosticsThreshold;
  /** Pixel error between predicted and server position. */
  errorOffset: DiagnosticsThreshold;
  /** Pixel distance between server-schema position and rendered sprite. */
  schemaRenderDist: DiagnosticsThreshold;
  /** Round-trip ping in ms. */
  pingMs: DiagnosticsThreshold;
  /** Number of unacknowledged input commands pending in the client buffer. */
  pendingInputs: DiagnosticsThreshold;
}

export const DIAGNOSTICS_DEFAULTS: DiagnosticsConfig = {
  corrections: { ok: 5, warn: 20 },
  errorOffset: { ok: 3, warn: 20 },
  schemaRenderDist: { ok: 10, warn: 50 },
  pingMs: { ok: 50, warn: 100 },
  pendingInputs: { ok: 10, warn: 30 },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to DIAGNOSTICS_DEFAULTS.
 *
 * Examples:
 *   pingMs: { ok: 200, warn: 400 }        // generous on a slow VPN
 *   corrections: { ok: 0, warn: 0 }       // strict QA — any correction is red
 */
export const DIAGNOSTICS_DEV_OVERRIDE: Partial<DiagnosticsConfig> = {};

export const DIAGNOSTICS: DiagnosticsConfig = {
  corrections: { ...DIAGNOSTICS_DEFAULTS.corrections, ...DIAGNOSTICS_DEV_OVERRIDE.corrections },
  errorOffset: { ...DIAGNOSTICS_DEFAULTS.errorOffset, ...DIAGNOSTICS_DEV_OVERRIDE.errorOffset },
  schemaRenderDist: { ...DIAGNOSTICS_DEFAULTS.schemaRenderDist, ...DIAGNOSTICS_DEV_OVERRIDE.schemaRenderDist },
  pingMs: { ...DIAGNOSTICS_DEFAULTS.pingMs, ...DIAGNOSTICS_DEV_OVERRIDE.pingMs },
  pendingInputs: { ...DIAGNOSTICS_DEFAULTS.pendingInputs, ...DIAGNOSTICS_DEV_OVERRIDE.pendingInputs },
};
